/**
 * product-custom-options.js
 * Handles custom product options rendered by product-custom-options.liquid
 */

(function () {
  'use strict';

  const state = {};
  const ORDER_TRACKING_RETURN_ORIGINS = [
    'https://lab.lostgear.xyz',
    'http://localhost:3000',
    'http://127.0.0.1:3000'
  ];

  function optionString(key, fallback, replacements) {
    var strings = window.lostgearStrings && window.lostgearStrings.productOptions;
    var value = strings && strings[key] ? strings[key] : fallback;
    Object.keys(replacements || {}).forEach(function (name) {
      value = value.replace('{{ ' + name + ' }}', replacements[name]);
    });
    return value;
  }

  function getOrderTrackingReturnTo() {
    try {
      var params = new URLSearchParams(window.location.search);
      var rawReturnTo = params.get('return_to');
      if (!rawReturnTo) return null;

      var returnToUrl = new URL(rawReturnTo, window.location.origin);
      var isAllowedOrigin =
        returnToUrl.origin === window.location.origin ||
        ORDER_TRACKING_RETURN_ORIGINS.indexOf(returnToUrl.origin) !== -1;
      var isOrderTrackingPath = /^\/(?:[a-z]{2}\/)?order-status\/[A-Za-z0-9]+$/.test(returnToUrl.pathname);

      if (!isAllowedOrigin || !isOrderTrackingPath) return null;
      return returnToUrl.toString();
    } catch (error) {
      console.warn('[lg-options] failed to parse return_to:', error);
      return null;
    }
  }

  function boot() {
    if (!window.lgOptions) return;
    Object.keys(window.lgOptions).forEach(function (sectionId) {
      initSection(sectionId);
    });
  }

  function initSection(sectionId) {
    var cfg = window.lgOptions[sectionId];
    if (!cfg) return;

    if (cfg.mode === 'configuration_selector') {
      initCompositeSection(sectionId, cfg);
      return;
    }

    initSingleSection(sectionId, cfg);
  }

  function initSingleSection(sectionId, cfg) {
    var container = document.getElementById('lg-options-' + sectionId);
    if (!container) return;

    state[sectionId] = {};
    primeStateFromCfg(sectionId, cfg);

    if (cfgHasVariantOptions(cfg)) {
      hideNativeVariantSelectors(sectionId);
      switchVariant(sectionId, cfg);
    }

    hydrateSubtitleLinks(container);
    bindEvents(sectionId, container, cfg);
    evaluateConditions(sectionId, container);
    updateCompatibleChoices(sectionId, container);
    updatePriceTable(sectionId, container, cfg);
    bindFormSubmit(sectionId);
    bindQtyControls(sectionId);
  }

  function initCompositeSection(sectionId, cfg) {
    var container = document.getElementById('lg-options-' + sectionId);
    if (!container) return;

    if (!cfg._lgBundleSelectionBound) {
      cfg._lgBundleSelectionBound = true;
      document.addEventListener('lg:bundle-selection-change', function (event) {
        var detail = event && event.detail ? event.detail : null;
        if (!detail || String(detail.sectionId || '') !== String(sectionId)) return;
        loadCompositeSelection(sectionId, Number(detail.selectedProductId || 0));
      });
    }

    bindFormSubmit(sectionId);
    bindQtyControls(sectionId);
    loadCompositeSelection(sectionId, Number(cfg.initialSelectedProductId || 0));
  }

  function loadCompositeSelection(sectionId, selectedProductId) {
    var cfg = window.lgOptions[sectionId];
    var container = document.getElementById('lg-options-' + sectionId);
    if (!cfg || !container || !selectedProductId) return;

    var hasRenderedContent = !!container.querySelector('.lg-options__sections, .lg-options__section-card, .lg-options__price-table');
    var requestToken = (cfg._compositeRequestToken || 0) + 1;
    cfg._compositeRequestToken = requestToken;

    if (hasRenderedContent) {
      startCompositeTransition(container);
    } else {
      container.innerHTML = '<div class="lg-options__loading">Loading options...</div>';
    }

    resolveCompositeConfig(cfg, selectedProductId)
      .then(function (runtime) {
        if (cfg._compositeRequestToken !== requestToken) return;
        cfg.runtime = runtime;
        renderCompositeRuntime(container, runtime, sectionId);
        primeStateFromCfg(sectionId, runtime);

        if (cfgHasVariantOptions(runtime)) {
          hideNativeVariantSelectors(sectionId);
          switchVariant(sectionId, runtime);
        }

        hydrateSubtitleLinks(container);
        bindEvents(sectionId, container, runtime);
        evaluateConditions(sectionId, container);
        updateCompatibleChoices(sectionId, container);
        updatePriceTable(sectionId, container, runtime);
        finishCompositeTransition(container);
      })
      .catch(function (error) {
        if (cfg._compositeRequestToken !== requestToken) return;
        console.error('[lg-options] composite init failed', error);
        stopCompositeTransition(container);
        container.innerHTML = '<div class="lg-options__loading">We could not load these configuration options. Please refresh and try again.</div>';
      });
  }

  function resolveCompositeConfig(cfg, selectedProductId) {
    return fetch(cfg.resolveCompositeEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        parentProduct: cfg.parentProduct,
        parentConfig: cfg.parentConfig,
        selectedProductId: selectedProductId,
        locale: cfg.locale || getCurrentLocale()
      })
    }).then(function (response) {
      return response.text().then(function (text) {
        var data = {};
        try { data = JSON.parse(text || '{}'); } catch (e) {}
        if (!response.ok) {
          throw new Error(data.error || ('Configuration resolve failed (' + response.status + ')'));
        }
        return data;
      });
    });
  }

  function getCurrentLocale() {
    var locale =
      (window.Shopify && window.Shopify.locale) ||
      document.documentElement.getAttribute('lang') ||
      '';

    return String(locale).replace('_', '-').split('-')[0].toLowerCase();
  }

  function renderCompositeRuntime(container, runtime, sectionId) {
    var sections = runtime && runtime.sections ? runtime.sections : [];
    var initialPrice = getInitialTargetPrice(runtime);
    var childSections = sections.filter(function (section) {
      return section.key !== 'parent';
    });
    var parentSections = sections.filter(function (section) {
      return section.key === 'parent';
    });

    container.classList.add('lg-options--composite');

    var html = '<div class="lg-options__sections">';

    html += childSections.map(function (section) {
      return '<div class="lg-options__section-card">' + renderSourceSection(section, sectionId) + '</div>';
    }).join('');

    html += parentSections.map(function (section) {
      return '<div class="lg-options__section-card">' + renderSourceSection(section, sectionId) + '</div>';
    }).join('');

    html += '</div>';

    if (initialPrice !== null) {
      html += renderPriceTable(initialPrice, sectionId);
    }

    container.innerHTML = html;
  }

  function startCompositeTransition(container) {
    if (!container) return;
    container.classList.add('is-switching');
    container.classList.remove('is-entering');
    container.setAttribute('aria-busy', 'true');
  }

  function stopCompositeTransition(container) {
    if (!container) return;
    container.classList.remove('is-switching');
    container.classList.remove('is-entering');
    container.removeAttribute('aria-busy');
    if (container._lgCompositeEnterTimer) {
      window.clearTimeout(container._lgCompositeEnterTimer);
      container._lgCompositeEnterTimer = null;
    }
  }

  function finishCompositeTransition(container) {
    if (!container) return;
    stopCompositeTransition(container);
    container.classList.add('is-entering');
    container._lgCompositeEnterTimer = window.setTimeout(function () {
      container.classList.remove('is-entering');
      container._lgCompositeEnterTimer = null;
    }, 320);
  }

  function getInitialTargetPrice(runtime) {
    if (!runtime || !runtime.activeTarget || !runtime.activeTarget.variants) return null;
    var initialVariantId = runtime.activeTarget.initialVariantId;
    var matched = null;
    if (initialVariantId) {
      matched = runtime.activeTarget.variants.find(function (variant) {
        return Number(variant.id) === Number(initialVariantId);
      });
    }
    if (!matched && runtime.activeTarget.variants.length) matched = runtime.activeTarget.variants[0];
    return matched ? Number(matched.price || 0) : null;
  }

  function renderPriceTable(basePrice, sectionId) {
    return '' +
      '<div class="lg-options__price-table" id="lg-price-table-' + escapeHtml(String(sectionId)) + '">' +
        '<div class="lg-options__price-rows">' +
          '<div class="lg-options__price-row" data-price-row="base">' +
            '<span class="lg-options__price-row-label">Base price</span>' +
            '<span class="lg-options__price-row-amount">' + formatMoney(basePrice) + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="lg-options__price-total">' +
          '<span class="lg-options__price-total-label">Total per unit</span>' +
          '<span class="lg-options__price-total-amount" data-base-price="' + escapeHtml(String(basePrice)) + '">' + formatMoney(basePrice) + '</span>' +
        '</div>' +
      '</div>';
  }

  function renderSourceSection(section, sectionId) {
    return '' +
      '<div class="lg-options__section" data-source-key="' + escapeHtml(section.key || '') + '">' +
        '<h3 class="lg-options__section-title">' + escapeHtml(section.title || '') + '</h3>' +
        (section.groups || []).map(function (group) {
          return renderGroup(group, sectionId);
        }).join('') +
      '</div>';
  }

  function renderGroup(group, sectionId) {
    return '' +
      '<div class="lg-options__group" data-group-id="' + escapeHtml(group.id || '') + '">' +
        '<div class="lg-options__group-header">' +
          '<span class="lg-options__group-icon">' + renderGroupIcon(group.icon) + '</span>' +
          '<span class="lg-options__group-label">' + escapeHtml(group.label || '') + '</span>' +
        '</div>' +
        '<div class="lg-options__group-body">' +
          (group.options || []).map(function (option) {
            return renderOption(option, sectionId);
          }).join('') +
        '</div>' +
      '</div>';
  }

  function renderOption(option, sectionId) {
    var hasCondition = !!(option.show_if && option.show_if.field);
    var classes = 'lg-options__option lg-options__option--' + escapeHtml(option.type || 'text');
    if (hasCondition) classes += ' lg-options__option--conditional';

    var attrs = '' +
      ' class="' + classes + '"' +
      ' data-option-id="' + escapeHtml(option.id || '') + '"' +
      ' data-type="' + escapeHtml(option.type || 'text') + '"';

    if (option.property_label) attrs += ' data-property-label="' + escapeHtml(option.property_label) + '"';
    if (hasCondition) {
      attrs += ' data-show-if-field="' + escapeHtml(option.show_if.field) + '"';
      attrs += ' data-show-if-values="' + escapeHtml((option.show_if.values || []).join(',')) + '"';
      attrs += ' hidden';
    }

    return '<div' + attrs + '>' +
      renderOptionInner(option, sectionId) +
      '<p class="lg-options__error" data-error-for="' + escapeHtml(option.id || '') + '" role="alert" hidden></p>' +
    '</div>';
  }

  function renderOptionInner(option, sectionId) {
    switch (option.type) {
      case 'select':
        return renderSelectOption(option);
      case 'swatch':
        return renderSwatchOption(option);
      case 'dropdown':
        return renderDropdownOption(option, sectionId);
      case 'file':
        return renderFileOption(option, sectionId);
      case 'text':
      default:
        return renderTextOption(option, sectionId);
    }
  }

  function renderTextOption(option, sectionId) {
    var maxChars = option.max_chars ? '' +
      '<span class="lg-options__char-count">' +
        '<span class="lg-options__char-current" data-option-id="' + escapeHtml(option.id || '') + '">0</span> / ' + escapeHtml(String(option.max_chars)) +
      '</span>' : '';

    var attrs = '';
    if (option.placeholder) attrs += ' placeholder="' + escapeHtml(option.placeholder) + '"';
    if (option.max_chars) attrs += ' maxlength="' + escapeHtml(String(option.max_chars)) + '"';
    if (option.required) attrs += ' data-required="true"';
    if (option.min_chars) attrs += ' data-min-chars="' + escapeHtml(String(option.min_chars)) + '"';
    if (option.validation_type) attrs += ' data-validation-type="' + escapeHtml(option.validation_type) + '"';
    if (option.validation && option.validation.pattern) {
      attrs += ' data-pattern="' + escapeHtml(option.validation.pattern) + '"';
      attrs += ' data-pattern-error="' + escapeHtml(option.validation.error || '') + '"';
    }

    return '' +
      '<label class="lg-options__label" for="lg-option-' + escapeHtml(option.id || '') + '-' + escapeHtml(String(sectionId)) + '">' +
        '<span class="lg-options__label-text">' + renderLabelText(option) + '</span>' +
        maxChars +
      '</label>' +
      '<div class="lg-options__input-wrap' + (option.validation_type === 'url' ? ' lg-options__input-wrap--icon' : '') + '">' +
        (option.validation_type === 'url' ? '' +
          '<span class="lg-options__input-icon">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>' +
          '</span>' : '') +
        '<input type="text" id="lg-option-' + escapeHtml(option.id || '') + '-' + escapeHtml(String(sectionId)) + '"' +
          ' class="lg-options__input' + (option.validation_type === 'url' ? ' lg-options__input--icon' : '') + '"' +
          ' data-option-id="' + escapeHtml(option.id || '') + '"' +
          attrs +
          ' autocomplete="off">' +
      '</div>' +
      renderSubtitle(option.subtitle);
  }

  function renderSelectOption(option) {
    var hasSubtitles = (option.choices || []).some(function (choice) { return !!choice.subtitle; });

    return '' +
      '<label class="lg-options__label">' +
        '<span class="lg-options__label-text">' + renderLabelText(option) + '</span>' +
      '</label>' +
      '<div class="lg-options__buttons' + (hasSubtitles ? ' lg-options__buttons--grid' : '') + '" data-option-id="' + escapeHtml(option.id || '') + '" role="group" aria-label="' + escapeHtml(option.label || '') + '">' +
        (option.choices || []).map(function (choice, index) {
          return '' +
            '<button type="button" class="lg-options__btn' + (index === 0 ? ' lg-options__btn--selected' : '') + '"' +
              ' data-option-id="' + escapeHtml(option.id || '') + '"' +
              ' data-value="' + escapeHtml(choice.value || '') + '"' +
              renderCompatibleWithAttrs(choice) +
              ' aria-pressed="' + (index === 0 ? 'true' : 'false') + '">' +
              '<span class="lg-options__btn-label">' +
                escapeHtml(choice.label || '') +
                (choice.price && Number(choice.price) > 0
                  ? '<span class="lg-options__btn-addon-price">+' + formatMoney(Math.round(Number(choice.price) * 100)) + '</span>'
                  : '') +
              '</span>' +
              (choice.subtitle ? '<span class="lg-options__btn-subtitle">' + escapeHtml(choice.subtitle) + '</span>' : '') +
            '</button>';
        }).join('') +
      '</div>' +
      renderSubtitle(option.subtitle);
  }

  function renderSwatchOption(option) {
    return '' +
      '<label class="lg-options__label">' +
        '<span class="lg-options__label-text">' + renderLabelText(option) + '</span>' +
      '</label>' +
      '<div class="lg-options__swatches" data-option-id="' + escapeHtml(option.id || '') + '" role="group" aria-label="' + escapeHtml(option.label || '') + '">' +
        (option.choices || []).map(function (choice, index) {
          return '' +
            '<div class="lg-options__swatch-item">' +
              '<button type="button" class="lg-options__swatch' + (index === 0 ? ' lg-options__swatch--selected' : '') + '"' +
                ' data-option-id="' + escapeHtml(option.id || '') + '"' +
                ' data-value="' + escapeHtml(choice.value || '') + '"' +
                ' title="' + escapeHtml(choice.label || '') + '"' +
                renderCompatibleWithAttrs(choice) +
                ' aria-pressed="' + (index === 0 ? 'true' : 'false') + '"' +
                ' style="--lg-swatch-color: ' + escapeHtml(choice.hex || 'var(--lg-surface-dark)') + '">' +
              '</button>' +
              '<span class="lg-options__swatch-label' + (index === 0 ? ' lg-options__swatch-label--active' : '') + '">' + escapeHtml(choice.label || '') + '</span>' +
            '</div>';
        }).join('') +
      '</div>' +
      renderSubtitle(option.subtitle);
  }

  function renderDropdownOption(option, sectionId) {
    var placeholder = option.placeholder || optionString('selectOption', 'Select an option');
    var startsWithPlaceholder = !!option.placeholder_only;

    return '' +
      '<label class="lg-options__label" for="lg-option-' + escapeHtml(option.id || '') + '-' + escapeHtml(String(sectionId)) + '">' +
        '<span class="lg-options__label-text">' + renderLabelText(option) + '</span>' +
      '</label>' +
      '<div class="lg-options__select-wrap">' +
        '<select id="lg-option-' + escapeHtml(option.id || '') + '-' + escapeHtml(String(sectionId)) + '"' +
          ' class="lg-options__select" data-option-id="' + escapeHtml(option.id || '') + '"' +
          (startsWithPlaceholder ? ' data-placeholder-only="true"' : '') +
          (option.required ? ' data-required="true"' : '') + '>' +
          (startsWithPlaceholder
            ? '<option value="" selected>' + escapeHtml(placeholder) + '</option>'
            : '') +
          (option.choices || []).map(function (choice, index) {
            var selected = !startsWithPlaceholder && index === 0 ? ' selected' : '';
            return '<option value="' + escapeHtml(choice.value || '') + '"' + selected + renderCompatibleWithAttrs(choice) + '>' + escapeHtml(choice.label || '') + '</option>';
          }).join('') +
        '</select>' +
        '<span class="lg-options__select-arrow">' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>' +
        '</span>' +
      '</div>' +
      renderSubtitle(option.subtitle);
  }

  function renderFileOption(option, sectionId) {
    return '' +
      '<div class="lg-options__file-wrap">' +
        '<label class="lg-options__file-trigger" for="lg-option-' + escapeHtml(option.id || '') + '-' + escapeHtml(String(sectionId)) + '">' +
          '<div class="lg-options__file-icon-wrap">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>' +
          '</div>' +
          '<div class="lg-options__file-text-wrap">' +
            '<span class="lg-options__file-label-text">' + renderLabelText(option) + '</span>' +
            '<span class="lg-options__file-name" data-option-id="' + escapeHtml(option.id || '') + '">No file chosen</span>' +
          '</div>' +
        '</label>' +
        '<input type="file" id="lg-option-' + escapeHtml(option.id || '') + '-' + escapeHtml(String(sectionId)) + '"' +
          ' class="lg-options__file-input" data-option-id="' + escapeHtml(option.id || '') + '"' +
          ' accept="' + escapeHtml(option.accept || '.ttf,.otf,.woff') + '"' +
          (option.required ? ' data-required="true"' : '') + '>' +
        (option.helper ? '<p class="lg-options__helper">' + escapeHtml(option.helper) + '</p>' : '') +
      '</div>';
  }

  function renderLabelText(option) {
    return '' +
      escapeHtml(option.label || '') +
      (option.required ? '<span class="lg-options__required">*</span>' : '');
  }

  function renderSubtitle(subtitle) {
    if (!subtitle) return '';
    return '<p class="lg-options__subtitle" data-subtitle="' + escapeHtml(subtitle) + '"></p>';
  }

  function renderCompatibleWithAttrs(choice) {
    if (!choice || !choice.compatible_with || !choice.compatible_with.option_id) return '';
    return '' +
      ' data-compatible-with-option="' + escapeHtml(choice.compatible_with.option_id) + '"' +
      ' data-compatible-with-values="' + escapeHtml((choice.compatible_with.values || []).join(',')) + '"';
  }

  function renderGroupIcon(icon) {
    switch (icon) {
      case 'text':
        return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>';
      case 'palette':
        return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="13.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="15.5" r="2.5"/><circle cx="8.5" cy="15.5" r="2.5"/></svg>';
      case 'nfc':
        return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8.32a7.43 7.43 0 0 1 0 7.36"/><path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58"/><path d="M12.91 4.1a15.91 15.91 0 0 1 .01 15.8"/><path d="M16.37 2a20.16 20.16 0 0 1 0 20"/></svg>';
      case 'clock':
        return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
      case 'star':
        return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
      case 'tag':
        return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';
      default:
        return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/></svg>';
    }
  }

  function primeStateFromCfg(sectionId, cfg) {
    var previous = state[sectionId] || {};
    var next = {};

    eachOption(cfg, function (option) {
      if (Object.prototype.hasOwnProperty.call(previous, option.id)) {
        next[option.id] = previous[option.id];
        return;
      }
      if (option.type === 'dropdown' && option.placeholder_only) {
        next[option.id] = '';
        return;
      }
      if (option.choices && option.choices.length) {
        next[option.id] = option.choices[0].value;
      } else {
        next[option.id] = '';
      }
    });

    state[sectionId] = next;
  }

  function cfgHasVariantOptions(cfg) {
    var found = false;
    eachOption(cfg, function (option) {
      if (option.variant_option) found = true;
    });
    return found;
  }

  function hideNativeVariantSelectors(sectionId) {
    var productForm = document.getElementById('product-form-' + sectionId);
    if (!productForm) return;

    var productEl = productForm.closest('.product') || document.querySelector('.product');
    if (!productEl) return;

    productEl.querySelectorAll('variant-selects, variant-radios').forEach(function (el) {
      el.style.display = 'none';
    });
  }

  function hydrateSubtitleLinks(container) {
    container.querySelectorAll('.lg-options__subtitle[data-subtitle]').forEach(function (el) {
      var text = el.dataset.subtitle || '';
      var iconSvg = '<svg class="lg-options__subtitle-link-icon" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
      el.innerHTML = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a class="lg-options__subtitle-link" href="$2" target="_blank" rel="noopener">$1' + iconSvg + '</a>');
    });
  }

  function eachGroup(cfg, fn) {
    if (cfg && Array.isArray(cfg.sections)) {
      cfg.sections.forEach(function (section) {
        (section.groups || []).forEach(function (group) {
          fn(group, section);
        });
      });
      return;
    }

    var groups = cfg && cfg.config ? cfg.config.groups : [];
    (groups || []).forEach(function (group) {
      fn(group, null);
    });
  }

  function eachOption(cfg, fn) {
    eachGroup(cfg, function (group, section) {
      (group.options || []).forEach(function (option) {
        fn(option, group, section);
      });
    });
  }

  function getOptionCfg(cfg, optionId) {
    var found = null;
    eachOption(cfg, function (option) {
      if (option.id === optionId) found = option;
    });
    return found;
  }

  function getTargetData(sectionId, cfg) {
    if (cfg && cfg.activeTarget) return cfg.activeTarget;
    var lgCfg = window.lgOptions[sectionId];
    return {
      variants: lgCfg && lgCfg.variants,
      variantOptions: lgCfg && lgCfg.variantOptions,
      initialVariantId: lgCfg && lgCfg.variants && lgCfg.variants[0] ? lgCfg.variants[0].id : null
    };
  }

  function normalizeKey(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function getVariantOptionNameCandidates(option) {
    var candidates = [];
    if (option.canonical_label) candidates.push(option.canonical_label);
    if (option.label) candidates.push(option.label);
    if (Array.isArray(option.variant_option_names)) {
      candidates = candidates.concat(option.variant_option_names);
    }
    if (option.source_product_title) candidates.push(option.source_product_title);
    if (option.source_product_handle) candidates.push(String(option.source_product_handle).replace(/[_-]+/g, ' '));
    return candidates.filter(Boolean);
  }

  function getVariantValueCandidates(choice) {
    return [choice && choice.canonical_label, choice && choice.label, choice && choice.value].filter(Boolean);
  }

  function getOrderedVariantOptionValues(variants, optionCount) {
    var ordered = [];

    for (var index = 0; index < optionCount; index++) {
      ordered[index] = [];
    }

    (variants || []).forEach(function (variant) {
      for (var index = 0; index < optionCount; index++) {
        var value = variant && variant['option' + (index + 1)];
        if (!value) continue;
        if (ordered[index].indexOf(value) === -1) {
          ordered[index].push(value);
        }
      }
    });

    return ordered;
  }

  function buildVariantResolution(sectionId, cfg) {
    var target = getTargetData(sectionId, cfg);
    var container = document.getElementById('lg-options-' + sectionId);
    var variants = target && target.variants;
    var variantOptions = target && target.variantOptions;
    var resolution = {
      targetProductId: target && target.productId ? target.productId : null,
      targetTitle: target && target.title ? target.title : '',
      targetHandle: target && target.handle ? target.handle : '',
      variantOptions: Array.isArray(variantOptions) ? variantOptions.slice() : [],
      selectedState: {},
      requirements: [],
      variants: Array.isArray(variants) ? variants.map(function (variant) {
        return {
          id: variant.id,
          title: variant.title,
          available: variant.available,
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3
        };
      }) : [],
      unresolved: false,
      matchedVariant: null,
      outcome: 'missing_target'
    };

    if (!variants || !variants.length || !variantOptions || !variantOptions.length) {
      return resolution;
    }

    var optionNameMap = {};
    var orderedVariantOptionValues = getOrderedVariantOptionValues(variants, variantOptions.length);
    var claimedVariantIndexes = {};
    variantOptions.forEach(function (name, index) {
      optionNameMap[normalizeKey(name)] = index;
    });

    eachOption(cfg, function (option) {
      if (!option.variant_option || !option.choices) return;

      var optionEl = container
        ? container.querySelector('.lg-options__option[data-option-id="' + option.id + '"]')
        : null;
      if (optionEl && optionEl.hidden) return;

      var selectedValue = state[sectionId][option.id];
      var choice = (option.choices || []).find(function (entry) { return entry.value === selectedValue; });
      var selectedChoiceIndex = (option.choices || []).findIndex(function (entry) { return entry.value === selectedValue; });
      var candidateNames = getVariantOptionNameCandidates(option);
      var optionIndex = -1;

      for (var i = 0; i < candidateNames.length; i++) {
        var normalizedName = normalizeKey(candidateNames[i]);
        if (Object.prototype.hasOwnProperty.call(optionNameMap, normalizedName)) {
          var matchedIndex = optionNameMap[normalizedName];
          if (!claimedVariantIndexes[matchedIndex]) {
            optionIndex = matchedIndex;
            break;
          }
        }
      }

      if (optionIndex === -1) {
        for (var fallbackIndex = 0; fallbackIndex < variantOptions.length; fallbackIndex++) {
          if (!claimedVariantIndexes[fallbackIndex]) {
            optionIndex = fallbackIndex;
            break;
          }
        }
      }

      if (optionIndex > -1) {
        claimedVariantIndexes[optionIndex] = true;
      }

      var candidateValues = choice ? getVariantValueCandidates(choice) : [];
      if (optionIndex > -1 && selectedChoiceIndex > -1) {
        var orderedValues = orderedVariantOptionValues[optionIndex] || [];
        if (orderedValues[selectedChoiceIndex]) {
          candidateValues.push(orderedValues[selectedChoiceIndex]);
        }
      }

      resolution.selectedState[option.id] = selectedValue;
      resolution.requirements.push({
        optionId: option.id,
        optionLabel: option.label || option.id,
        sourceProduct: option.source_product_title || '',
        selectedValue: selectedValue,
        selectedChoiceLabel: choice ? choice.label : null,
        candidateNames: candidateNames,
        resolvedVariantOption: optionIndex > -1 ? variantOptions[optionIndex] : null,
        resolvedVariantIndex: optionIndex,
        selectedChoiceIndex: selectedChoiceIndex,
        candidateValues: candidateValues,
        status: !choice ? 'missing_choice' : optionIndex === -1 ? 'missing_option_name' : 'mapped'
      });

      if (!choice || optionIndex === -1) {
        resolution.unresolved = true;
      }
    });

    if (!resolution.requirements.length) {
      resolution.outcome = 'no_variant_requirements';
      return resolution;
    }

    if (resolution.unresolved) {
      resolution.outcome = 'unresolved_requirements';
      return resolution;
    }

    for (var variantIndex = 0; variantIndex < variants.length; variantIndex++) {
      var variant = variants[variantIndex];
      var matches = true;
      for (var requirementIndex = 0; requirementIndex < resolution.requirements.length; requirementIndex++) {
        var requirement = resolution.requirements[requirementIndex];
        var variantValue = normalizeKey(variant['option' + (requirement.resolvedVariantIndex + 1)]);
        var candidateValues = (requirement.candidateValues || []).map(normalizeKey);
        if (candidateValues.indexOf(variantValue) === -1) {
          matches = false;
          break;
        }
      }
      if (matches) {
        resolution.matchedVariant = variant;
        resolution.outcome = 'matched';
        return resolution;
      }
    }

    resolution.outcome = 'no_variant_match';
    return resolution;
  }

  function logVariantResolution(sectionId, cfg, resolution) {
    if (!cfg || !cfg.activeTarget || !resolution) return;

    try {
      console.groupCollapsed('[lg-options] bundle resolution');
      console.log({
        outcome: resolution.outcome,
        targetProductId: resolution.targetProductId,
        targetTitle: resolution.targetTitle,
        targetHandle: resolution.targetHandle,
        variantOptions: resolution.variantOptions,
        selectedState: resolution.selectedState,
        requirements: resolution.requirements,
        variants: resolution.variants,
        matchedVariant: resolution.matchedVariant ? {
          id: resolution.matchedVariant.id,
          title: resolution.matchedVariant.title,
          option1: resolution.matchedVariant.option1,
          option2: resolution.matchedVariant.option2,
          option3: resolution.matchedVariant.option3
        } : null
      });
      console.groupEnd();
    } catch (error) {
      console.warn('[lg-options] bundle resolution logging failed', error);
    }
  }

  function findMatchingVariant(sectionId, cfg) {
    var resolution = buildVariantResolution(sectionId, cfg);
    logVariantResolution(sectionId, cfg, resolution);
    return resolution.matchedVariant || null;
  }

  function switchVariant(sectionId, cfg) {
    var matched = findMatchingVariant(sectionId, cfg);
    if (!matched) return null;

    var productForm = document.getElementById('product-form-' + sectionId);
    if (!productForm) return matched;

    var idInput = productForm.querySelector('input[name="id"]');
    if (idInput) idInput.value = matched.id;
    return matched;
  }

  function updateCompatibleChoices(sectionId, container) {
    var selected = {};
    container.querySelectorAll('.lg-options__swatch--selected').forEach(function (el) {
      selected[el.dataset.optionId] = el.dataset.value;
    });
    container.querySelectorAll('.lg-options__btn--selected').forEach(function (el) {
      selected[el.dataset.optionId] = el.dataset.value;
    });
    container.querySelectorAll('.lg-options__select').forEach(function (el) {
      selected[el.dataset.optionId] = el.value;
    });

    var restrictions = {};
    var selectors = ['.lg-options__swatch', '.lg-options__btn', '.lg-options__select option'];
    selectors.forEach(function (selector) {
      container.querySelectorAll(selector).forEach(function (el) {
        var compatOptionId = el.dataset.compatibleWithOption;
        var compatValues = el.dataset.compatibleWithValues;
        if (!compatOptionId || !compatValues) return;

        var sourceOptionId = el.dataset.optionId ||
          (el.closest('.lg-options__select') && el.closest('.lg-options__select').dataset.optionId);
        if (!sourceOptionId) return;
        if (selected[sourceOptionId] !== el.dataset.value && el.tagName !== 'OPTION') return;
        if (el.tagName === 'OPTION' && el.closest('select').value !== el.value) return;

        var allowed = compatValues.split(',').map(function (value) { return value.trim(); });
        if (!restrictions[compatOptionId]) restrictions[compatOptionId] = new Set();
        allowed.forEach(function (value) { restrictions[compatOptionId].add(value); });
      });
    });

    container.querySelectorAll('.lg-options__swatches').forEach(function (group) {
      var optionId = group.dataset.optionId;
      var allowed = restrictions[optionId] || null;
      group.querySelectorAll('.lg-options__swatch-item').forEach(function (item) {
        var btn = item.querySelector('.lg-options__swatch');
        if (!btn) return;
        item.style.display = (!allowed || allowed.has(btn.dataset.value)) ? '' : 'none';
      });
      var selectedBtn = group.querySelector('.lg-options__swatch--selected');
      if (selectedBtn && selectedBtn.closest('.lg-options__swatch-item').style.display === 'none') {
        var first = group.querySelector('.lg-options__swatch-item:not([style*="display: none"]) .lg-options__swatch');
        if (first) first.click();
      }
    });

    container.querySelectorAll('.lg-options__buttons').forEach(function (group) {
      var optionId = group.dataset.optionId;
      var allowed = restrictions[optionId] || null;
      group.querySelectorAll('.lg-options__btn').forEach(function (btn) {
        btn.style.display = (!allowed || allowed.has(btn.dataset.value)) ? '' : 'none';
      });
      var selectedBtn = group.querySelector('.lg-options__btn--selected');
      if (selectedBtn && selectedBtn.style.display === 'none') {
        var first = group.querySelector('.lg-options__btn:not([style*="display: none"])');
        if (first) first.click();
      }
    });

    container.querySelectorAll('.lg-options__select').forEach(function (select) {
      var optionId = select.dataset.optionId;
      var allowed = restrictions[optionId] || null;
      select.querySelectorAll('option').forEach(function (opt) {
        var show = !allowed || allowed.has(opt.value);
        opt.hidden = !show;
        opt.disabled = !show;
      });
      var selectedOpt = select.querySelector('option:checked');
      if (selectedOpt && selectedOpt.hidden) {
        var first = select.querySelector('option:not([hidden])');
        if (first) {
          select.value = first.value;
          select.dispatchEvent(new Event('change'));
        }
      }
    });
  }

  function bindEvents(sectionId, container, cfg) {
    container.querySelectorAll('.lg-options__btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var optionId = this.dataset.optionId;
        container.querySelectorAll('.lg-options__btn[data-option-id="' + optionId + '"]').forEach(function (entry) {
          entry.classList.remove('lg-options__btn--selected');
          entry.setAttribute('aria-pressed', 'false');
        });
        this.classList.add('lg-options__btn--selected');
        this.setAttribute('aria-pressed', 'true');
        state[sectionId][optionId] = this.dataset.value;
        clearError(container, optionId);
        switchVariant(sectionId, cfg);
        evaluateConditions(sectionId, container);
        updateCompatibleChoices(sectionId, container);
        updatePriceTable(sectionId, container, cfg);
      });
    });

    container.querySelectorAll('.lg-options__swatch').forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        var optionId = this.dataset.optionId;
        container.querySelectorAll('.lg-options__swatch[data-option-id="' + optionId + '"]').forEach(function (entry) {
          entry.classList.remove('lg-options__swatch--selected');
          entry.setAttribute('aria-pressed', 'false');
        });
        this.classList.add('lg-options__swatch--selected');
        this.setAttribute('aria-pressed', 'true');
        this.closest('.lg-options__swatches').querySelectorAll('.lg-options__swatch-label').forEach(function (label) {
          label.classList.remove('lg-options__swatch-label--active');
        });
        var itemLabel = this.closest('.lg-options__swatch-item').querySelector('.lg-options__swatch-label');
        if (itemLabel) itemLabel.classList.add('lg-options__swatch-label--active');
        state[sectionId][optionId] = this.dataset.value;
        clearError(container, optionId);
        switchVariant(sectionId, cfg);
        evaluateConditions(sectionId, container);
        updateCompatibleChoices(sectionId, container);
        updatePriceTable(sectionId, container, cfg);
      });
    });

    container.querySelectorAll('.lg-options__input').forEach(function (input) {
      var optionId = input.dataset.optionId;
      input.addEventListener('input', function () {
        state[sectionId][optionId] = this.value;
        var counter = container.querySelector('.lg-options__char-current[data-option-id="' + optionId + '"]');
        if (counter) counter.textContent = this.value.length;
        clearError(container, optionId);
      });
      input.addEventListener('blur', function () {
        validateField(container, optionId, this.value, getOptionCfg(cfg, optionId));
      });
    });

    container.querySelectorAll('.lg-options__select').forEach(function (select) {
      var optionId = select.dataset.optionId;
      state[sectionId][optionId] = select.value;
      select.addEventListener('change', function () {
        state[sectionId][optionId] = this.value;
        clearError(container, optionId);
        switchVariant(sectionId, cfg);
        evaluateConditions(sectionId, container);
        updateCompatibleChoices(sectionId, container);
        updatePriceTable(sectionId, container, cfg);
      });
    });

    container.querySelectorAll('.lg-options__file-input').forEach(function (input) {
      var optionId = input.dataset.optionId;
      input.addEventListener('change', function () {
        var file = this.files[0] || null;
        state[sectionId][optionId] = file;
        var nameEl = container.querySelector('.lg-options__file-name[data-option-id="' + optionId + '"]');
        if (nameEl) nameEl.textContent = file ? file.name : optionString('noFile', 'No file chosen');
        clearError(container, optionId);
      });
    });
  }

  function evaluateConditions(sectionId, container) {
    container.querySelectorAll('.lg-options__option--conditional').forEach(function (el) {
      var field = el.dataset.showIfField;
      var allowed = [];
      if (el.dataset.showIfValues) {
        allowed = el.dataset.showIfValues.split(',').map(function (value) { return value.trim(); }).filter(Boolean);
      } else if (el.dataset.showIfValue) {
        allowed = [el.dataset.showIfValue.trim()];
      }

      var current = state[sectionId][field];
      var shouldShow = allowed.length > 0 && allowed.indexOf(current) !== -1;

      if (el.hidden === shouldShow) {
        el.hidden = !shouldShow;
        if (!shouldShow) {
          var optionId = el.dataset.optionId;
          var type = el.dataset.type;
          if (type === 'text' || type === 'file') {
            state[sectionId][optionId] = '';
            var input = el.querySelector('.lg-options__input, .lg-options__file-input');
            if (input) input.value = '';
            var nameEl = el.querySelector('.lg-options__file-name');
            if (nameEl) nameEl.textContent = optionString('noFile', 'No file chosen');
          }
          clearError(container, optionId);
        }
      }
    });
  }

  function updatePriceTable(sectionId, container, cfg) {
    var totalEl = container.querySelector('.lg-options__price-total-amount');
    var rowsEl = container.querySelector('.lg-options__price-rows');
    if (!totalEl || !rowsEl) return;

    rowsEl.querySelectorAll('[data-price-row="addon"]').forEach(function (row) { row.remove(); });

    var matched = findMatchingVariant(sectionId, cfg);
    var currentPrice = matched ? matched.price : parseInt(totalEl.dataset.basePrice, 10);

    var hasRows = false;
    eachOption(cfg, function (option) {
      if (!option.variant_option || !option.choices) return;
      var optionEl = container.querySelector('.lg-options__option[data-option-id="' + option.id + '"]');
      if (optionEl && optionEl.hidden) return;
      var selectedValue = state[sectionId][option.id];
      var choice = option.choices.find(function (entry) { return entry.value === selectedValue; });
      if (choice && choice.price && Number(choice.price) > 0) {
        var row = document.createElement('div');
        row.className = 'lg-options__price-row lg-options__price-row--addon';
        row.setAttribute('data-price-row', 'addon');
        row.innerHTML =
          '<span class="lg-options__price-row-label">' + escapeHtml(choice.label) + '</span>' +
          '<span class="lg-options__price-row-amount">+' + formatMoney(Math.round(Number(choice.price) * 100)) + '</span>';
        rowsEl.appendChild(row);
        hasRows = true;
      }
    });

    totalEl.textContent = formatMoney(currentPrice);

    var table = container.querySelector('.lg-options__price-table');
    if (table) {
      table.classList.toggle('lg-options__price-table--visible', hasRows);
    }
  }

  function validateAll(sectionId, container, cfg) {
    var valid = true;
    eachOption(cfg, function (option) {
      var optionEl = container.querySelector('.lg-options__option[data-option-id="' + option.id + '"]');
      if (optionEl && optionEl.hidden) return;
      var value = state[sectionId][option.id];
      if (!validateField(container, option.id, value, option)) valid = false;
    });
    return valid;
  }

  function validateField(container, optionId, value, option) {
    if (!option) return true;

    if (option.required) {
      if (option.type === 'text' && (!value || String(value).trim() === '')) {
        showError(container, optionId, option.label + ' is required');
        return false;
      }
      if (option.type === 'dropdown' && (!value || String(value).trim() === '')) {
        showError(container, optionId, optionString('selectRequired', 'Please select {{ option }}', { option: option.label }));
        return false;
      }
      if (option.type === 'file' && !value) {
        showError(container, optionId, optionString('uploadRequired', 'Please upload a file'));
        return false;
      }
    }

    var VALIDATION_PRESETS = {
      email: { re: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, msg: optionString('validEmail', 'Enter a valid email address') },
      url: { re: /^https?:\/\/[^\s]+$/, msg: optionString('validUrl', 'Enter a valid URL (starting with https://)') },
      username: { re: /^[a-zA-Z0-9_\-]{2,32}$/, msg: optionString('validUsername', 'Username: 2–32 characters, letters/numbers/underscore/hyphen only') },
      phone: { re: /^\+?[\d\s\-().]{7,20}$/, msg: optionString('validPhone', 'Enter a valid phone number') },
      number: { re: /^\d+(\.\d+)?$/, msg: optionString('validNumber', 'Enter a valid number') }
    };

    if (option.type === 'text' && value && String(value).trim() !== '') {
      if (option.min_chars && String(value).trim().length < parseInt(option.min_chars, 10)) {
        showError(container, optionId, optionString('minimumCharacters', 'Minimum {{ count }} characters required', { count: option.min_chars }));
        return false;
      }
      if (option.validation_type && VALIDATION_PRESETS[option.validation_type]) {
        var preset = VALIDATION_PRESETS[option.validation_type];
        if (!preset.re.test(String(value).trim())) {
          showError(container, optionId, preset.msg);
          return false;
        }
      } else if (option.validation && option.validation.pattern) {
        var re = new RegExp(option.validation.pattern);
        if (!re.test(value)) {
          showError(container, optionId, option.validation.error || optionString('invalidInput', 'Invalid input'));
          return false;
        }
      }
    }

    clearError(container, optionId);
    return true;
  }

  function showError(container, optionId, message) {
    var el = container.querySelector('[data-error-for="' + optionId + '"]');
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    var input = container.querySelector('.lg-options__input[data-option-id="' + optionId + '"]');
    if (input) input.classList.add('lg-options__input--error');
  }

  function clearError(container, optionId) {
    var el = container.querySelector('[data-error-for="' + optionId + '"]');
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
    var input = container.querySelector('.lg-options__input[data-option-id="' + optionId + '"]');
    if (input) input.classList.remove('lg-options__input--error');
  }

  function bindFormSubmit(sectionId) {
    var productForm = document.getElementById('product-form-' + sectionId);
    if (!productForm || productForm.dataset.lgOptionsSubmitBound === '1') return;
    productForm.dataset.lgOptionsSubmitBound = '1';

    productForm.addEventListener('submit', function (e) {
      var container = document.getElementById('lg-options-' + sectionId);
      var cfg = getCurrentCfg(sectionId);
      if (!container || !cfg) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      clearProductFormError(productForm);

      var isCheckout = e.submitter ? e.submitter.name !== 'add' : false;

      if (!validateAll(sectionId, container, cfg)) {
        var firstError = container.querySelector('.lg-options__error:not([hidden])');
        if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      var submitBtn = productForm.querySelector('[type="submit"]');
      setLoading(productForm, submitBtn, true);

      handleSubmit(sectionId, container, cfg, productForm, isCheckout)
        .catch(function (err) { console.error('[lg-options] submit error', err); })
        .finally(function () { setLoading(productForm, submitBtn, false); });
    }, { capture: true });
  }

  function getCurrentCfg(sectionId) {
    var cfg = window.lgOptions[sectionId];
    if (!cfg) return null;
    return cfg.runtime || cfg;
  }

  async function handleSubmit(sectionId, container, cfg, productForm, isCheckout) {
    var matched = switchVariant(sectionId, cfg);
    if (cfgHasVariantOptions(cfg) && !matched) {
      showProductFormError(productForm, optionString('combinationUnavailable', 'The selected combination is not available. Please choose a different option set.'));
      return;
    }

    var properties = buildProperties(sectionId, container, cfg);
    var existingProperties = collectFormProperties(productForm);
    Object.keys(existingProperties).forEach(function (key) {
      if (properties[key] === undefined) properties[key] = existingProperties[key];
    });
    var idInput = productForm.querySelector('input[name="id"]');
    var mainVariantId = parseInt(idInput && idInput.value, 10);
    if (!mainVariantId) {
      showProductFormError(productForm, optionString('variantMissing', 'Could not determine which variant to add to cart.'));
      return;
    }

    var qty = getQty(sectionId);
    var cartDrawer = document.querySelector('cart-drawer');
    var cartNotification = document.querySelector('cart-notification');
    var cart = cartDrawer || cartNotification;
    var returnTo = getOrderTrackingReturnTo();

    var formData = new FormData();
    formData.append('id', mainVariantId);
    formData.append('quantity', qty);
    Object.keys(properties).forEach(function (key) {
      formData.append('properties[' + key + ']', properties[key]);
    });
    eachOption(cfg, function (option) {
      var optionEl = container.querySelector('.lg-options__option[data-option-id="' + option.id + '"]');
      if (optionEl && optionEl.hidden) return;
      if (skipOptionProperty(cfg, option)) return;

      var value = state[sectionId][option.id];
      if (!(value instanceof File)) return;

      formData.append('properties[' + getPropertyKey(option) + ']', value, value.name);
    });

    var allSectionIds = [];
    [cartNotification, cartDrawer].forEach(function (el) {
      if (el && typeof el.getSectionsToRender === 'function') {
        el.getSectionsToRender().forEach(function (section) {
          if (allSectionIds.indexOf(section.id) === -1) allSectionIds.push(section.id);
        });
      }
    });
    if (allSectionIds.length > 0) {
      formData.append('sections', allSectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    if (cart && typeof cart.setActiveElement === 'function') {
      cart.setActiveElement(document.activeElement);
    }

    if (cfg && cfg.activeTarget) {
      var submitResolution = buildVariantResolution(sectionId, cfg);
      console.log('[lg-options] cart add request', {
        stage: 'request',
        url: (window.routes.cart_add_url || '/cart/add').replace(/\.js$/, '') + '.js',
        isCheckout: !!isCheckout,
        idInputValue: idInput && idInput.value,
        mainVariantId: mainVariantId,
        quantity: qty,
        properties: properties,
        payload: {
          id: String(mainVariantId),
          quantity: String(qty)
        },
        variantResolution: {
          outcome: submitResolution.outcome,
          matchedId: submitResolution.matchedVariant ? submitResolution.matchedVariant.id : null,
          matchedTitle: submitResolution.matchedVariant ? submitResolution.matchedVariant.title : null,
          option1: submitResolution.matchedVariant ? submitResolution.matchedVariant.option1 : null,
          option2: submitResolution.matchedVariant ? submitResolution.matchedVariant.option2 : null,
          option3: submitResolution.matchedVariant ? submitResolution.matchedVariant.option3 : null,
          variantOptions: submitResolution.variantOptions,
          selectedState: submitResolution.selectedState,
          requirements: submitResolution.requirements
        }
      });
      Object.keys(properties).forEach(function (key) {
        console.log('[lg-options] cart add property', key, properties[key]);
      });
    }

    var cartAddUrl = (window.routes.cart_add_url || '/cart/add').replace(/\.js$/, '') + '.js';
    var response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: formData
    });

    var responseText = await response.text();
    var data;
    try { data = JSON.parse(responseText); }
    catch (e) { throw new Error('Cart add ' + response.status + ' non-JSON: ' + responseText.slice(0, 200)); }

    if (cfg && cfg.activeTarget) {
      console.log('[lg-options] cart add response', {
        stage: 'response',
        url: cartAddUrl,
        status: response.status,
        ok: response.ok,
        mainVariantId: mainVariantId,
        responseData: data,
        rawResponseText: responseText
      });
    }

    if (data.status) {
      showProductFormError(productForm, data.description || data.message || optionString('addError', 'Could not add to cart'));
      return;
    }

    if (isCheckout) {
      window.location = window.routes.checkout_url || '/checkout';
      return;
    }

    if (returnTo) {
      window.location.assign(returnTo);
      return;
    }

    if (cart) {
      if (cartNotification) {
        if (cartNotification.classList.contains('is-empty')) cartNotification.classList.remove('is-empty');
        try { cartNotification.renderContents(data); } catch (e) { console.warn('[lg] notification renderContents failed:', e.message); }
      }
      if (cartDrawer) {
        if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
        try { cartDrawer.renderContents(data); } catch (e) { console.warn('[lg] drawer renderContents failed:', e.message); }
      }
    } else {
      window.location = returnTo || window.routes.cart_url || '/cart';
      return;
    }
  }

  function buildProperties(sectionId, container, cfg) {
    var props = {};

    eachOption(cfg, function (option) {
      var optionEl = container.querySelector('.lg-options__option[data-option-id="' + option.id + '"]');
      if (optionEl && optionEl.hidden) return;
      if (skipOptionProperty(cfg, option)) return;
      var value = state[sectionId][option.id];
      if (value instanceof File) return;
      if (value === null || value === undefined || value === '') return;
      props[getPropertyKey(option)] = getPropertyDisplayValue(option, value);
    });

    return props;
  }

  function getPropertyKey(option) {
    return (option && (option.property_label || option.label)) || '';
  }

  function skipOptionProperty(cfg, option) {
    return !!(cfg && cfg.activeTarget && option && option.variant_option);
  }

  function getPropertyDisplayValue(option, value) {
    if (option && Array.isArray(option.choices)) {
      var selectedChoice = option.choices.find(function (choice) {
        return String(choice.value || '') === String(value);
      });
      if (selectedChoice && selectedChoice.label) {
        return String(selectedChoice.label);
      }
    }
    return String(value);
  }

  function collectFormProperties(productForm) {
    var props = {};
    if (!productForm) return props;

    Array.prototype.slice.call(
      productForm.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]')
    ).forEach(function (field) {
      if (!field.name) return;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
      if (field.disabled) return;

      var match = field.name.match(/^properties\[(.*)\]$/);
      if (!match || !match[1]) return;

      var value = field.value;
      if (value === null || value === undefined || value === '') return;

      props[match[1]] = String(value);
    });

    return props;
  }

  function showProductFormError(productForm, message) {
    var wrapper = productForm.closest('product-form') && productForm.closest('product-form').querySelector('.product-form__error-message-wrapper');
    if (!wrapper) return;
    wrapper.removeAttribute('hidden');
    var msgEl = wrapper.querySelector('.product-form__error-message');
    if (msgEl) msgEl.textContent = message || optionString('addError', 'Could not add to cart');
  }

  function clearProductFormError(productForm) {
    var wrapper = productForm.closest('product-form') && productForm.closest('product-form').querySelector('.product-form__error-message-wrapper');
    if (!wrapper) return;
    wrapper.setAttribute('hidden', 'hidden');
    var msgEl = wrapper.querySelector('.product-form__error-message');
    if (msgEl) msgEl.textContent = '';
  }

  function setLoading(form, btn, loading) {
    if (!btn) return;
    var spinner = form.querySelector('.loading__spinner');
    if (loading) {
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency, minimumFractionDigits: 2 });
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function bindQtyControls(sectionId) {
    var qtyEl = document.querySelector('[data-lg-quantity][data-section-id="' + sectionId + '"]');
    if (!qtyEl || qtyEl.dataset.qtyBound) return;
    qtyEl.dataset.qtyBound = '1';
    var valueEl = qtyEl.querySelector('[data-qty-val]');
    var form = qtyEl.closest('form');
    var inputEl = form && form.querySelector('[data-lg-quantity-input]');
    qtyEl.querySelectorAll('[data-qty-action]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var current = parseInt(valueEl.textContent, 10) || 1;
        var next = this.dataset.qtyAction === 'plus' ? current + 1 : Math.max(1, current - 1);
        valueEl.textContent = next;
        if (inputEl) inputEl.value = next;
      });
    });
  }

  function getQty(sectionId) {
    var qtyEl = document.querySelector('[data-lg-quantity][data-section-id="' + sectionId + '"]');
    if (!qtyEl) return 1;
    return parseInt(qtyEl.querySelector('[data-qty-val]').textContent, 10) || 1;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function bootQty() {
    document.querySelectorAll('[data-lg-quantity][data-section-id]').forEach(function (qtyEl) {
      bindQtyControls(qtyEl.dataset.sectionId);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootQty);
  } else {
    bootQty();
  }
})();
